import "../../styles/Dataset.css";
import "../../styles/org-buttons.css";
import axios from "axios";
import { useEffect, useState, memo, useMemo, useCallback, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { DatasetWorkspaceGroupContext } from "./DatasetWorkspaceShell";
import { useNavigate, useLocation } from "react-router-dom";
import DeleteDoudleConfirmModal from "./DeleteDoudleConfirmModal";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import { VIDEO_EXTS, AUDIO_EXTS } from "../../constant/FileType";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import EmptyDataset from "./EmptyDataset";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import Timer from "../Timer";
import { useDebounce } from "../../utils/useDebounce";
import { usePagination } from "../../utils/usePagination";
import LoadingSpinner from "../LoadingSpinner";
import Pagination from "../Pagination";
import audioWaveformImg from "../../assets/audio_waveform.svg";
import { useUploadManager } from "../../utils/UploadContext";
import { openFiftyOne } from "../../utils/fiftyone";
import { loadUiSettings } from "../../utils/uiSettings";
import { buttonText } from "../../utils/buttonText";
import { pageText } from "../../utils/pageText";
import { notificationText } from "../../utils/notificationText";

function isTableDataset(item) {
  return (
    item?.media_type === "table" ||
    item?.folder_type === "table" ||
    item?.group === "table"
  );
}

function getDatasetSampleCount(item) {
  // table 无 FO samples，优先用 total_samples
  if (isTableDataset(item) && typeof item.total_samples === "number") {
    return item.total_samples;
  }
  const samples = item.samples;
  if (Array.isArray(samples)) {
    return samples.length;
  }
  if (samples && typeof samples.length === "number") {
    return samples.length;
  }
  if (typeof item.total_samples === "number") {
    return item.total_samples;
  }
  return 0;
}

function getDatasetPreviewSrc(item) {
  const src = item.preview_image;
  if (!src || !/^(data:|blob:|https?:\/\/|\/)/.test(src)) {
    return null;
  }
  return src;
}

const RESERVED_GROUPS = new Set(["report", "image", "audio", "table", "Trash"]);

function getCustomGroupNames(datasetItems) {
  const names = new Set();
  datasetItems.forEach((item) => {
    if (item.group && !RESERVED_GROUPS.has(item.group)) {
      names.add(item.group);
    }
  });
  return Array.from(names).sort();
}

function Datasets(props) {
  const workspaceGroup = useContext(DatasetWorkspaceGroupContext);
  const {
    CustomizedContent,
    GlobalCustomizedContent,
    hideCreateButton,
    extraButtons,
    overviewTitle,
    overviewDescription,
    selectedGroup: selectedGroupProp,
    onGroupSelect: onGroupSelectProp,
    onGroupsChange: onGroupsChangeProp,
  } = props;
  const selectedGroup = selectedGroupProp ?? workspaceGroup?.selectedGroup;
  const onGroupSelect = onGroupSelectProp ?? workspaceGroup?.onGroupSelect;
  const onGroupsChange = onGroupsChangeProp ?? workspaceGroup?.onGroupsChange;

  const [datasetItems, setDatasetItems] = useState([]);
  const [checkedDatasetItems, setCheckedDatasetItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = loadUiSettings().datasetsPerPage;

  const [datasetsLoading, setDatasetsLoading] = useState(true);

  const userToken = Cookies.get("user_token");
  const userData = userToken ? jwtDecode(userToken) : null;
  const role = userData ? userData.role : null;
  const organization = userData ? userData.organization : null;

  const loadDatasetList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setDatasetsLoading(true);
    const userToken = Cookies.get("user_token");
    try {
      const response = await axios({
        method: "post",
        url: "/datasets/list",
        data: { requesterToken: userToken },
        headers: {
          "Content-Type": "application/json",
        },
      });
      setDatasetItems(response.data.concat());
    } catch (error) {
      const errorMsg = error.response?.data || error.message;
      if (!silent) window.alert(errorMsg);
    } finally {
      if (!silent) setDatasetsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDatasetList();
  }, [loadDatasetList]);

  const navigate = useNavigate();

  if (!userData) {
    navigate("/signin");
  }

  const authorizedRoles = ["admin", "developer", "annotator"];

  if (!authorizedRoles.includes(role)) {
    return (
      <div>
        <h1>{pageText("Insufficient permissions")}</h1>
        <p>
          {pageText("Your role is")} <strong>"{role}"</strong>
          {pageText("and you cannot make any changes to the dataset.")}
        </p>
        <p>
          {pageText("If you want to operate the dataset, please contact the admin user to help upgrade your role to")}{" "}
          <strong>"developer"</strong>.
        </p>
      </div>
    );
  }

  const datasetStats = {
    total: datasetItems.length,
    image: datasetItems.filter((item) => item.media_type === "image").length,
    audio: datasetItems.filter((item) => item.media_type === "audio").length,
    table: datasetItems.filter((item) => item.media_type === "table" || item.folder_type === "table").length,
    samples: datasetItems.reduce((sum, item) => sum + getDatasetSampleCount(item), 0),
    trash: datasetItems.filter((item) => item.group === "Trash").length,
  };

  return (
    <div className="datasets">
      <DatasetOverview
        stats={datasetStats}
        loading={datasetsLoading}
        title={overviewTitle}
        description={overviewDescription}
      />
      <InputSet
        loadDatasetList={loadDatasetList}
        datasetItems={datasetItems}
        checkedDatasetItems={checkedDatasetItems}
        setCheckedDatasetItems={setCheckedDatasetItems}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setCurrentPage={setCurrentPage}
        hideCreateButton={hideCreateButton}
        extraButtons={extraButtons}
      />
      {GlobalCustomizedContent && (
        <GlobalCustomizedContent
          loadDatasetList={loadDatasetList}
          checkedDatasetItems={checkedDatasetItems}
          setCheckedDatasetItems={setCheckedDatasetItems}
        />
      )}
      <DatasetGallery
        loadDatasetList={loadDatasetList}
        datasetItems={datasetItems}
        CustomizedContent={CustomizedContent}
        checkedDatasetItems={checkedDatasetItems}
        setCheckedDatasetItems={setCheckedDatasetItems}
        datasetsLoading={datasetsLoading}
        searchTerm={searchTerm}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        itemsPerPage={itemsPerPage}
        selectedGroup={selectedGroup}
        onGroupSelect={onGroupSelect}
        onGroupsChange={onGroupsChange}
      />
    </div>
  );
}

function DatasetOverview({ stats, loading, title, description }) {
  const displayTitle = pageText(title || "Datasets");
  const displayDescription =
    pageText(description || "Manage image, audio, and table datasets for MLOps workflows.");

  const overviewItems = [
    { label: "Datasets", value: stats.total, tone: "blue" },
    { label: "Images", value: stats.image, tone: "green" },
    { label: "Audio", value: stats.audio, tone: "violet" },
    { label: "Samples", value: stats.samples.toLocaleString(), tone: "orange" },
    { label: "Tables", value: stats.table, tone: "slate" },
  ];

  return (
    <section className="dataset-overview" aria-label={pageText("Dataset overview")}>
      <div className="dataset-page-heading">
        <div>
          <p className="dataset-kicker">{pageText("Workspace")}</p>
          <h1>{displayTitle}</h1>
          <p>{displayDescription}</p>
        </div>
        <span className="org-count-badge">
          {pageText(loading ? "Syncing" : `${stats.total} datasets`)}
        </span>
      </div>
      <div className="dataset-metric-grid">
        {overviewItems.map((item) => (
          <div className={`dataset-metric-card metric-${item.tone}`} key={item.label}>
            <span className="metric-label">{pageText(item.label)}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

const LinkStyle = {
  color: "blue",
  textDecoration: "underline",
  cursor: "pointer",
};

const frameRateTooltip = (
  <div>
    <p>
      <a
        onClick={() => {
          const w = window.open("about:blank");
          w.location.href = `https://en.wikipedia.org/wiki/Frame_rate`;
        }}
        style={LinkStyle}
      >
        <strong>Frame Rate</strong>
      </a>{" "}
      is typically the frequency at which consecutive images are captured or
      displayed.{" "}
    </p>
    <p>
      If the input frame rate is greater than 5, it will take 5 to convert to
      frames.
    </p>
  </div>
);

function InputSet(props) {
  const { 
    loadDatasetList, 
    datasetItems, 
    checkedDatasetItems, 
    setCheckedDatasetItems,
    searchTerm,
    setSearchTerm,
    setCurrentPage,
    hideCreateButton,
    extraButtons,
  } = props;
  const { startFileUpload } = useUploadManager();

  const [datasetName, setDatasetName] = useState("");
  const [mediatype, setMediatype] = useState("image");
  const [deleteMethod, setDeleteMethod] = useState(null);
  const [mergeDatasetName, setMergeDatasetName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selectedDatasets, setSelectedDatasets] = useState([]);

  const [showCreateDatasetModal, setShowCreateDatasetModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const userToken = Cookies.get("user_token");
  const userData = userToken ? jwtDecode(userToken) : null;
  const organization = userData ? userData.organization : null;

  const [file, setFile] = useState(null);
  const [fileDragActive, setFileDragActive] = useState(false);
  const [fps, setFps] = useState("");
  const [imgQuality, setImgQuality] = useState(100);
  const [fileLoading, setFileLoading] = useState(false);

  const handleShowCreateDatasetModal = () => {
    setShowCreateDatasetModal(true);
  };

  const handleCloseCreateDatasetModal = () => {
    setShowCreateDatasetModal(false);
  };

  const handleCloseMergeModal = () => {
    setShowMergeModal(false);
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
  };

  const createDataset = async () => {
    if (!datasetName) {
      window.alert("Please input a dataset name.");
      return false;
    }
    let url = `/datasets/create?dataset_name=${datasetName}`;
    if (mediatype) {
      url += `&media_type=${mediatype}`;
    }
    if (mediatype==='report') {
      url += `&group=report`;
    }
    if (mediatype==='audio') {
      url += `&group=audio`;
    }
    let method = userToken ? "post" : "put";
    let data = userToken ? { requesterToken: userToken } : null;
    try {
      await axios({
        method: method,
        url: url,
        data: data,
      });
      setTimeout(() => {
        loadDatasetList();
      }, 500);
      return true;
    } catch (error) {
      const errorMsg = error?.response?.data || error?.message || "Create failed.";
      window.alert(errorMsg);
      return false;
    }
  };

  const mergeDataset = () => {
    if (!checkedDatasetItems || checkedDatasetItems.length === 0) {
      window.alert("Please select dataset.");
      return;
    }
    setSelectedDatasets(checkedDatasetItems);
    setShowMergeModal(true);
  };

  const groupDataset = () => {
    if (!checkedDatasetItems || checkedDatasetItems.length === 0) {
      window.alert("Please select dataset.");
      return;
    }
    setSelectedDatasets(checkedDatasetItems);
    setShowGroupModal(true);
  };

  const performMerge = async () => {
    if (!mergeDatasetName) {
      window.alert("Please input a dataset name.");
      return;
    }
    axios
      .post(
        `/datasets/merge?dataset_name=${mergeDatasetName}`,
        selectedDatasets
      )
      .then((response) => {
        setMergeDatasetName("");
        setSelectedDatasets([]);
        setShowMergeModal(false);
        loadDatasetList();
        window.alert(`Datasets have been merged successfully.`);
      })
      .catch((error) => {
        let errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const moveToGroup = async () => {
    if (!groupName) {
      window.alert("Please input a group name.");
      return;
    }
    let url = `/datasets/group?group_name=${groupName}`;
    perfromGroup(url);
  };

  const removeFromGroup = async () => {
    let url = `/datasets/group`;
    perfromGroup(url);
  };

  const perfromGroup = async (url) => {
    axios
      .post(url, selectedDatasets)
      .then((response) => {
        setGroupName(null);
        setSelectedDatasets([]);
        setShowGroupModal(false);
        loadDatasetList();
        window.alert(`Move datasets to/from group successfully.`);
      })
      .catch((error) => {
        let errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const deleteAllDataset = async () => {
    setDeleteAllLoading(true);
    const method = userToken ? "post" : "delete";
    const data = userToken ? { requesterToken: userToken } : null;
    axios({
      method: method,
      url: `/datasets/delete`,
      data: data,
    })
      .then((response) => {
        setDeleteAllLoading(false);
        window.alert(
          `The deletion of all datasets was completed successfully.`
        );
        loadDatasetList();
      })
      .catch((error) => {
        setDeleteAllLoading(false);
        let errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const deleteSelected = async () => {
    if (!checkedDatasetItems || checkedDatasetItems.length === 0) {
      window.alert("Please select dataset.");
      return;
    }

    const toMove = [];
    const toDelete = [];

    // checkedDatasetItems.forEach((datasetName) => {
    //   const item = datasetItems.find((item) => item.name === datasetName);
    //   if (item && item.group!== "Trash") {
    //       toMove.push(datasetName);
    //   } else {
    //       toDelete.push(datasetName);
    //   }
    // });

    checkedDatasetItems.forEach((datasetName) => {
      const item = datasetItems.find((item) => item.name === datasetName);
      if (item && item.group!== "Trash") {
          toMove.push({
            name: datasetName,
            media_type: item?.media_type
          });
      } else {
          toDelete.push({
            name: datasetName,
            media_type: item?.media_type,
            organization: item?.organization
          });
      }
    });

    // 处理需要转移的数据集
    if (toMove.length > 0) {
      try {
        const response = await axios({
            method: 'post',
            url: `/datasets/group?group_name=Trash`,
            data: toMove,
        });
        window.alert(`The selected datasets have been moved to Trash.`);
        loadDatasetList();
        setCheckedDatasetItems([]);
      } catch (error) {
          let errorMsg = error.response?.data || 'Failed to move dataset to Trash.';
          window.alert(errorMsg);
      }
    }

    // 处理需要删除的数据集
    if (toDelete.length > 0) {
      try {
        const response = await axios.post(`/datasets/delete/selected`, toDelete);
        window.alert(`The selected datasets have been successfully deleted.`);
        loadDatasetList();
        setCheckedDatasetItems([]);
      } catch (error) {
            let errorMsg = error.response?.data || 'Failed to delete dataset.';
            window.alert(errorMsg);
      }
    }
  };

  const handleChangeDatasetName = (e) => {
    let newValue = e.target.value;
    setDatasetName(newValue);
  };

  const mediaTypeDisplay = (
    <div className="media-type-select-option-container">
      <label htmlFor="media-type-select">media type:</label>
      <select
        id="media-type-select"
        value={mediatype}
        onChange={(e) => {
          setMediatype(e.target.value);
        }}
        className="media-type-select-option"
      >
        <option value="image">image</option>
        {/* <option value="video">video</option> */}
        <option value="audio">audio</option>
        {/* <option value="report">report</option> */}
      </select>
    </div>
  );

  // upload dataset
  const handleChangeImgQuality = (e) => {
    let newSplitNum = e.target.value;
    if (newSplitNum < 1 || newSplitNum > 100) {
      if (!newSplitNum) {
        setImgQuality(100);
      }
      return;
    }
    setImgQuality(newSplitNum);
  };

  const isVideoFile = () => {
    if (!file || !file.name) {
      return false;
    }
    const exts = file.name.split(".").pop();
    return VIDEO_EXTS.includes(exts);
  };

  const selectUploadFile = (selected) => {
    if (!selected) return;
    setFile(selected);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFileDragActive(false);
    if (fileLoading) return;
    const dropped = e.dataTransfer?.files?.[0];
    selectUploadFile(dropped);
  };

  const uploadImage = () => {
    if (!file) {
      window.alert("Please upload a file.");
      return;
    }
    let url = `/file/upload?folder_name=${datasetName}&`;
    if (fps) {
      url += `&fps=${fps}`;
    }
    if (imgQuality) {
      if (imgQuality < 1 || imgQuality > 100) {
        window.alert("Image quality should between 1 and 100.");
        return;
      }
      url += `&image_quality=${imgQuality}`;
    }
    startFileUpload({
      url,
      file,
      successMessage: `File '${file.name}' upload success.`,
      onSuccess: () => loadDatasetList(),
    });
    handleCloseCreateDatasetModal();
  };

  const uploadreport = async () => {
    if (file.type !== 'application/json') {
      alert("For report, only JSON files are allowed.");
      return;
    }

    setFileLoading(true);

    try {
      const reader = new FileReader();
      reader.onload = function(event) {
        const result = JSON.parse(event.target.result);
        const datasetPath = result.dataset;

        // 假设这里有一个API来处理JSON文件的上传和解析
        // const formData = new FormData();
        // formData.append("file", file);
        // formData.append("dataset_path", datasetPath);

        const params = {
          folder_name: datasetName,
          dataset_path: datasetPath
        };    
        axios.put(`/file/upload-report-dataset`,params)
          .then(response => {
            setFileLoading(false);
            let msg = `Report file '${file.name}' uploaded and copied successfully.`;
            alert(msg);
          })
          .catch(copyError => {
            setFileLoading(false);
            let copyErrorMsg = copyError.response ? copyError.response.data : "Copy failed.";
            alert(copyErrorMsg);
          });
      };
      reader.readAsText(file);
      setTimeout(() => {
        setFileLoading(false);
        loadDatasetList();
      }, 500); // 这里设置为0.5秒
      handleCloseCreateDatasetModal();
    } catch (error) {
      setFileLoading(false);
      let errorMsg = error.message;
      alert(errorMsg);
    }
  };

  // 判断是否为音频文件
  const isAudioFile = () => {
    if (!file || !file.name) {
      return false;
    }
    const exts = file.name.split(".").pop().toLowerCase();
    return AUDIO_EXTS.includes(exts);
  };

  // 上传音频文件
  const uploadAudio = () => {
    if (!file) {
      window.alert("Please upload the audio file.");
      return;
    }
    startFileUpload({
      url: `/file/upload-audio?folder_name=${datasetName}`,
      file,
      successMessage: `The audio file '${file.name}' uploaded successfully.`,
      onSuccess: () => loadDatasetList(),
    });
    handleCloseCreateDatasetModal();
  };

  const role = userData ? userData.role : null;

  const CreateDatasetModal = (
    <Modal
      show={showCreateDatasetModal}
      onHide={handleCloseCreateDatasetModal}
      size="lg"
      className="dataset-enterprise-modal"
    >
      <Modal.Header closeButton closeLabel={buttonText("Close")} className="dataset-modal-header">
        <Modal.Title>Create Dataset</Modal.Title>
      </Modal.Header>
      <Modal.Body className="dataset-modal-body">
        <div className="dataset-modal-form">
          <div className="dataset-modal-field">
            <label htmlFor="dataset-name">Dataset Name</label>
            <Form.Control
              type="text"
              id="dataset-name"
              placeholder="Enter dataset name"
              value={datasetName}
              onChange={handleChangeDatasetName}
            />
          </div>

          <div className="dataset-modal-field">
            <label htmlFor="media-type-select">Media Type</label>
            {mediaTypeDisplay}
          </div>

          <div className="dataset-modal-field">
            <label htmlFor="file-upload">Upload File</label>
            <div
              className={`dataset-file-dropzone${
                fileDragActive ? " is-drag-active" : ""
              }${fileLoading ? " is-disabled" : ""}`}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!fileLoading) setFileDragActive(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!fileLoading) setFileDragActive(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFileDragActive(false);
              }}
              onDrop={handleFileDrop}
            >
              <input
                id="file-upload"
                type="file"
                disabled={fileLoading}
                onChange={(e) => selectUploadFile(e.target.files[0])}
              />
              <div className="dataset-file-input">
                <label htmlFor="file-upload" className="dataset-file-browse">
                  Browse
                </label>
                <span className="dataset-file-name">
                  {file ? file.name : "No file chosen"}
                </span>
              </div>
              <p className="dataset-file-drop-hint">
                Drag and drop a file or zip here, or click Browse
              </p>
            </div>
          </div>

          {file && isVideoFile() && (
            <div className="dataset-modal-panel">
              <div className="dataset-modal-field">
                <label htmlFor="frame-rate">
                  <OverlayTrigger
                    overlay={
                      <Tooltip id="tooltip-bottom" placement="bottom">
                        {frameRateTooltip}
                      </Tooltip>
                    }
                    placement="bottom"
                  >
                    <a
                      onClick={() => {
                        const w = window.open("about:blank");
                        w.location.href = `https://en.wikipedia.org/wiki/Frame_rate`;
                      }}
                      className="dataset-modal-link"
                    >
                      Frame Rate
                    </a>
                  </OverlayTrigger>
                </label>
                <input
                  id="frame-rate"
                  type="number"
                  value={fps}
                  onChange={(e) => setFps(e.target.value)}
                />
              </div>
              <div className="dataset-modal-field">
                <label htmlFor="image-quality">Image Quality (%)</label>
                <input
                  id="image-quality"
                  type="number"
                  onChange={handleChangeImgQuality}
                />
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="org-modal-footer dataset-modal-footer">
        <p className="dataset-modal-tip">
          After starting upload you can switch pages; progress continues in the
          corner.
        </p>

        <div className="dataset-modal-actions">
          <button
            type="button"
            className="org-btn org-btn-primary"
            onClick={async () => {
              const created = await createDataset();
              if (!created) return;

              setTimeout(() => {
                if (mediatype === "report") {
                  uploadreport();
                } else if (mediatype === "audio") {
                  uploadAudio();
                } else {
                  uploadImage();
                }
              }, 1000);
            }}
            disabled={fileLoading}
          >
            {buttonText(fileLoading ? <Timer loadingStates={[fileLoading]} /> : "Create Dataset")}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );

  const mergeModal = (
    <Modal show={showMergeModal} onHide={handleCloseMergeModal}>
      <Modal.Header closeButton closeLabel={buttonText("Close")}>
        <Modal.Title>Merge Datasets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Control
          type="text"
          id="merge-dataset-name"
          placeholder="New Dataset Name"
          value={mergeDatasetName}
          onChange={(e) => setMergeDatasetName(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer className="org-modal-footer">
        <button type="button" className="org-btn org-btn-secondary" onClick={handleCloseMergeModal}>
          {buttonText("Close")}
        </button>
        <button type="button" className="org-btn org-btn-primary" onClick={performMerge}>
          {buttonText("Merge")}
        </button>
      </Modal.Footer>
    </Modal>
  );

  const groupModal = (
    <Modal show={showGroupModal} onHide={handleCloseGroupModal}>
      <Modal.Header closeButton closeLabel={buttonText("Close")}>
        <Modal.Title>Group Management</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Selected {selectedDatasets.length} dataset(s)</p>
        <Form.Group>
          <Form.Label>Group Name:</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="org-modal-footer">
        <button type="button" className="org-btn org-btn-secondary" onClick={handleCloseGroupModal}>
          {buttonText("Cancel")}
        </button>
        <button type="button" className="org-btn org-btn-danger" onClick={removeFromGroup}>
          {buttonText("Remove from Group")}
        </button>
        <button type="button" className="org-btn org-btn-primary" onClick={moveToGroup}>
          {buttonText("Move to Group")}
        </button>
      </Modal.Footer>
    </Modal>
  );

  const isTrashGroup = checkedDatasetItems.some((datasetName) => {
    const item = datasetItems.find((item) => item.name === datasetName);
    return item && item.group === "Trash";
  });

  return (
    <div>
      <div className="dataset-toolbar">
        <div className="dataset-toolbar-actions">
          <button
            type="button"
            className="org-btn org-btn-secondary"
            onClick={groupDataset}
          >
            {buttonText("Group")}
          </button>
          <button
            type="button"
            className="org-btn org-btn-danger"
            onClick={() => {
              setDeleteMethod("deleteSelected");
              setModalShow(true);
            }}
          >
            {buttonText("Delete Selected")}
          </button>
          {role && role === "admin" && (
            <button
              type="button"
              className="org-btn org-btn-danger"
              onClick={() => {
                setDeleteMethod("deleteAll");
                setModalShow(true);
              }}
              disabled={deleteAllLoading}
            >
              {buttonText(deleteAllLoading ? "Loading..." : "Delete All")}
            </button>
          )}
          {extraButtons}
          {!hideCreateButton && (
            <button
              type="button"
              className="org-btn org-btn-primary"
              onClick={handleShowCreateDatasetModal}
            >
              {buttonText("+ Create Dataset")}
            </button>
          )}
        </div>

        <div className="dataset-toolbar-search">
          <label htmlFor="dataset-search">{pageText("Search")}</label>
          <Form.Control
            type="text"
            id="dataset-search"
            placeholder={pageText("Search Dataset...")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>
      {mergeModal}
      {groupModal}
      {CreateDatasetModal}
      <DeleteDoudleConfirmModal
        show={modalShow}
        isTrashGroup={isTrashGroup}
        onHide={() => setModalShow(false)}
        resetDeleteMethod={() => setDeleteMethod(null)}
        deleteMethod={deleteMethod}
        onDelete={deleteAllDataset}
        onDeleteSelected={deleteSelected}
      />
    </div>
  );  
}

const DatasetCards = memo(function DatasetCards(props) {
  const {
    items, isActive, getFilteredItems, itemsPerPage, currentPage,
    setTotalPages, setCurrentPage, debouncedSearchTerm, CustomizedContent,
    loadDatasetList, dbMapList, deleteDataset, checkedDatasetItems,
    setCheckedDatasetItems,
  } = props;

  // 过滤items
  const filteredItems = useMemo(() => getFilteredItems(items), [items, getFilteredItems]);

  // 计算分页数据
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;

  // 确保 currentPage 不超出范围
  const safePage = Math.min(currentPage, totalPages);
  const pageForRender = isActive ? safePage : 1;
  const startIndex = (pageForRender - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredItems.slice(startIndex, endIndex);

  // 仅当前激活 tab 可以更新页数信息
  useEffect(() => {
    if (isActive) {
      setTotalPages(totalPages);
    }
  }, [isActive, totalPages, setTotalPages]);

  // 仅当前激活 tab 在页码超出范围时修正
  useEffect(() => {
    if (isActive && currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [isActive, currentPage, totalPages, setCurrentPage]);

  if (filteredItems.length === 0 && debouncedSearchTerm) {
    return (
      <div className="dataset-empty-state">
        <h4>{pageText("No matching dataset found")}</h4>
        <p>{pageText("Try adjusting your search keywords")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="datasetGallery">
        {currentData &&
          currentData.map((item) => {
            return (
              <DatasetItem
                key={`dataset-${item.name}`}
                item={item}
                CustomizedContent={CustomizedContent}
                loadDatasetList={loadDatasetList}
                dbMapList={dbMapList}
                deleteDataset={deleteDataset}
                checkedDatasetItems={checkedDatasetItems}
                setCheckedDatasetItems={setCheckedDatasetItems}
              />
            );
          })}
      </div>
      {isActive && filteredItems.length > 0 && (
        <div className="dataset-gallery-summary">
          {pageText(`Showing ${currentData.length} of ${filteredItems.length} datasets`)}
        </div>
      )}
      {isActive && filteredItems.length > itemsPerPage && (
        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          hasNextPage={safePage < totalPages}
          hasPrevPage={safePage > 1}
        />
      )}
      {isActive && (
        <div className="dataset-list-footer">
          <span>{pageText(`Total ${filteredItems.length} items`)}</span>
        </div>
      )}
    </div>
  );
});

function DatasetGallery(props) {
  const {
    loadDatasetList,
    datasetItems,
    CustomizedContent,
    checkedDatasetItems,
    setCheckedDatasetItems,
    datasetsLoading,
    searchTerm,
    currentPage,
    setCurrentPage,
    setTotalPages,
    itemsPerPage,
    selectedGroup,
    onGroupSelect,
    onGroupsChange,
  } = props;

  const [dbMapList, setDbMapList] = useState([]);
  const [showClearTrashModal, setShowClearTrashModal] = useState(false);
  const [trashClearLoading, setTrashClearLoading] = useState(false);
  // 防抖搜索
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const userToken = Cookies.get("user_token");
  const userData = userToken ? jwtDecode(userToken) : null;
  const role = userData ? userData.role : null;
  const organization = userData ? userData.organization : null;

  const loadDbMapList = async () => {
    axios
      .get("/kneron/global_constant/dbmap/list")
      .then((response) => {
        setDbMapList(response.data);
      })
      .catch((error) => {
        let errorMsg = error.response.data;
        window.alert(errorMsg);
      });
  };

  const deleteDataset = async (item) => {
    let groupName = item?.group?? '';
    let datasetName = item.name;
    let mediaType = item.media_type;
    const method = userToken ? "post" : "delete";
    const data = userToken ? { requesterToken: userToken } : null;

    const updateDatasetListAndSelection = () => {
        loadDatasetList();
        let newCheckedDatasetItems = checkedDatasetItems.filter(
            (name) => name!== datasetName
        );
        setCheckedDatasetItems(newCheckedDatasetItems.slice());
    };

    let data_info = {
      name: datasetName,
      media_type: mediaType
    }
    if (groupName!== 'Trash') {
        // 如果不在 trash group tab 内，将数据集转移到 trash group
        try {
            const response = await axios({
                method: 'post',
                url: `/datasets/group?group_name=Trash`,
                data: [data_info], // 传递要转移的数据集名称
            });
            updateDatasetListAndSelection();
            window.alert(`Dataset "${datasetName}" has been moved to Trash.`);
        } catch (error) {
            let errorMsg = error.response?.data || 'Failed to move dataset to Trash.';
            window.alert(errorMsg);
        }
    } else {
        try {
            const response = await axios({
                method: method,
                url: `/datasets/delete?dataset_name=${datasetName}&media_type=${mediaType}`,
                data: data,
            });
            updateDatasetListAndSelection();
            window.alert(`Dataset "${datasetName}" has been deleted successfully.`);
        } catch (error) {
            let errorMsg = error.response?.data || 'Failed to delete dataset.';
            window.alert(errorMsg);
          }
      }
  };

  useEffect(() => {
    loadDbMapList();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      setCurrentPage(1);
    }
  }, [selectedGroup, setCurrentPage]);

  const groupItems = {};
  datasetItems.forEach((item) => {
    if (item.group) {
      if (!groupItems[item.group]) {
        groupItems[item.group] = [];
      }
      groupItems[item.group].push(item);
    }
  });

  let commonDatasetItems = datasetItems.filter(
    (item) =>
      item.folder !== "Yes" &&
      item.table !== "Yes" &&
      item.group !== "Trash" &&
      !isTableDataset(item)
  );

  if (role === "annotator") {
    commonDatasetItems = commonDatasetItems.filter(
      (item) => item.media_type === "image" || item.media_type === "audio"
    );
  }
  const folderDatasetItems = datasetItems.filter(
    (item) => item.folder === "Yes"
  );
  const tableDatasetItems = datasetItems.filter(
    (item) =>
      item.group === "table" ||
      (item.group !== "Trash" &&
        (item.media_type === "table" || item.folder_type === "table"))
  );
  // const restDatasetItems = commonDatasetItems.filter((item) => !item.group);
  const imageDatasetItems = commonDatasetItems.filter(
    (item) => item.media_type === "image"
  );
  const videoDatasetItems = commonDatasetItems.filter(
    (item) => item.media_type === "video"
  );
  const ReportDatasetItems = commonDatasetItems.filter(
    (item) => item.group === "report"
  )
  const AudioDatasetItems = commonDatasetItems.filter(
    (item) => item.media_type === "audio"
  )

  // 过滤数据（添加搜索功能）
  const getFilteredItems = useCallback((items) => {
    if (!debouncedSearchTerm) return items;
    const searchLower = debouncedSearchTerm.toLowerCase();
    return items.filter(item => 
      item.name?.toLowerCase().includes(searchLower) ||
      item.media_type?.toLowerCase().includes(searchLower) ||
      item.group?.toLowerCase().includes(searchLower)
    );
  }, [debouncedSearchTerm]);


  const tabItems = {
    // ALL: datasetItems,
    ALL: commonDatasetItems,
    IMAGE: imageDatasetItems,
    // VIDEO: videoDatasetItems,
    // FOLDER: folderDatasetItems,
    AUDIO: AudioDatasetItems,
    TABLE: tableDatasetItems,
    // Report: ReportDatasetItems,
  };
  let displayTabItems;
  if (role === "annotator") {
    displayTabItems = {
      ALL: commonDatasetItems,
      IMAGE: imageDatasetItems,
      AUDIO: AudioDatasetItems,
      TABLE: tableDatasetItems
    };
  } else {
    displayTabItems = tabItems;
  }

  useEffect(() => {
    onGroupsChange?.([
      ...Object.keys(displayTabItems),
      ...getCustomGroupNames(datasetItems),
    ]);
  }, [datasetItems, displayTabItems, onGroupsChange]);

  useEffect(() => {
    if (!selectedGroup) return;
    const isValidGroup =
      selectedGroup === "Trash" ||
      Object.prototype.hasOwnProperty.call(displayTabItems, selectedGroup) ||
      getCustomGroupNames(datasetItems).includes(selectedGroup);
    if (!isValidGroup) {
      onGroupSelect?.("ALL");
    }
  }, [datasetItems, displayTabItems, selectedGroup, onGroupSelect]);

  const clearTrash = async () => {
    if (!userToken) {
      window.alert("Not logged in or token missing; please sign in again.");
      return;
    }

    setTrashClearLoading(true);
    try {
      const response = await axios.post("/datasets/trash/clear", {
        requesterToken: userToken,
      });
      const failedCount = response.data?.failed?.length || 0;
      setShowClearTrashModal(false);
      setCheckedDatasetItems([]);
      await loadDatasetList();
      if (failedCount > 0) {
        window.alert(
          `Trash was partially cleared. ${failedCount} item(s) could not be deleted.`
        );
      } else {
        window.alert("The Trash has been successfully cleaned up.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || error.message || "Failed to empty Trash.";
      window.alert(errorMsg);
    } finally {
      setTrashClearLoading(false);
    }
  };

  const deleteGroup = async (groupName) => {
    const groupDatasets = datasetItems
      .filter(item => item.group === groupName)
      .map(item => ({
        name: item.name,
        media_type: item.media_type
      }));

    // 删除Group时不传group_name参数，这样后端会调用remove_from_group()
    const url = `/datasets/group`;
    axios({
      method: 'delete',
      url: url,
      data: groupDatasets,
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((response) => {
        window.alert(`Delete group '${groupName}' success.`);
        loadDatasetList();
        setCheckedDatasetItems([]);
        if (selectedGroup === groupName) {
          onGroupSelect?.(null);
        }
      })
      .catch((error) => {
        const errorMsg = error.response?.data || 'Failed to delete group.';
        window.alert(errorMsg);
      });
  };

  const deleteTabBtnStyle = {
    marginLeft: "5px",
    width: "0.7rem",
    height: "0.7rem",
  };

  // 搜索提示（如果有搜索词）
  const SearchHint = searchTerm && (
    <div className="dataset-search-hint">
      <span>
        {pageText(`Search: "${searchTerm}"`)}
      </span>
    </div>
  );

  const selectedGroupKey = selectedGroup || "ALL";
  const selectedGroupItems =
    displayTabItems[selectedGroupKey] || groupItems[selectedGroupKey] || [];
  const selectedGroupLabel = pageText(selectedGroupKey);
  const isCustomGroup = getCustomGroupNames(datasetItems).includes(selectedGroupKey);
  const isTrashGroup = selectedGroupKey === "Trash";

  const clearTrashModal = (
    <Modal
      show={showClearTrashModal}
      onHide={() => !trashClearLoading && setShowClearTrashModal(false)}
      centered
    >
      <Modal.Header
        closeButton={!trashClearLoading}
        closeLabel={buttonText("Close")}
      >
        <Modal.Title>{notificationText("Empty Trash")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          {notificationText(
            "All datasets and tables in the current organization's Trash will be permanently deleted. This action cannot be undone."
          )}
        </p>
      </Modal.Body>
      <Modal.Footer className="org-modal-footer">
        <button
          type="button"
          className="org-btn org-btn-secondary"
          disabled={trashClearLoading}
          onClick={() => setShowClearTrashModal(false)}
        >
          {buttonText("Cancel")}
        </button>
        <button
          type="button"
          className="org-btn org-btn-danger"
          disabled={trashClearLoading}
          onClick={clearTrash}
        >
          <FontAwesomeIcon icon={faTrashCan} />
          {buttonText(trashClearLoading ? "Emptying..." : "Empty Trash")}
        </button>
      </Modal.Footer>
    </Modal>
  );

  const GroupDataset = (
    <div>
      {SearchHint}
      <div className="dataset-group-header">
        <h3>{selectedGroupLabel}</h3>
        {isTrashGroup ? (
          <button
            type="button"
            className="org-btn org-btn-danger dataset-empty-trash-button"
            disabled={selectedGroupItems.length === 0 || trashClearLoading}
            onClick={() => setShowClearTrashModal(true)}
          >
            <FontAwesomeIcon icon={faTrashCan} />
            {buttonText(trashClearLoading ? "Emptying..." : "Empty Trash")}
          </button>
        ) : isCustomGroup ? (
          <button
            onClick={() => deleteGroup(selectedGroupKey)}
            type="button"
            className="btn-close"
            style={deleteTabBtnStyle}
            aria-label={buttonText(`Delete group ${selectedGroupLabel}`)}
          />
        ) : null}
      </div>
      {clearTrashModal}
      <DatasetCards
        items={selectedGroupItems}
        isActive={true}
        getFilteredItems={getFilteredItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setTotalPages={setTotalPages}
        setCurrentPage={setCurrentPage}
        debouncedSearchTerm={debouncedSearchTerm}
        CustomizedContent={CustomizedContent}
        loadDatasetList={loadDatasetList}
        dbMapList={dbMapList}
        deleteDataset={deleteDataset}
        checkedDatasetItems={checkedDatasetItems}
        setCheckedDatasetItems={setCheckedDatasetItems}
      />
    </div>
  );

  let display;
  if (datasetsLoading) {
    display = <LoadingSpinner text="data loading..." />;
  } else if (datasetItems.length === 0) {
    display = (
      <div className="dataset-empty-state">
        <h1>{pageText("No dataset in your workspace.")}</h1>
        <p>{pageText("Please click + Create Dataset to create one.")}</p>
      </div>
    );
  } else {
    display = GroupDataset;
  }

  return <div className="Tabs-datasetGallery">{display}</div>;
}

function DatasetCardPreview({ item, sampleCount }) {
  const src = getDatasetPreviewSrc(item);
  const isAudio = item.media_type === "audio";
  const isTable = isTableDataset(item);

  if (isTable) {
    return (
      <div className="dataset-card-preview is-empty">
        <span>{pageText(sampleCount > 0 ? `${sampleCount} files` : "Table dataset")}</span>
      </div>
    );
  }

  if (isAudio) {
    if (sampleCount === 0) {
      return (
        <div className="dataset-card-preview is-empty">
          <span>{pageText("Empty dataset")}</span>
        </div>
      );
    }
    return (
      <div
        className="dataset-card-preview has-audio-waveform"
        role="img"
        aria-label={`${item.name} audio preview`}
        style={{ backgroundImage: `url("${audioWaveformImg}")` }}
      />
    );
  }

  if (!src && sampleCount === 0) {
    return (
      <div className="dataset-card-preview is-empty">
        <span>{pageText("Empty dataset")}</span>
      </div>
    );
  }

  return (
    <div
      className={`dataset-card-preview${src ? " has-image" : ""}`}
      role={src ? "img" : undefined}
      aria-label={src ? `${item.name} preview` : undefined}
      style={src ? { backgroundImage: `url("${src}")` } : undefined}
    />
  );
}

const DatasetItem = memo(function DatasetItem(props) {
  const {
    item,
    CustomizedContent,
    loadDatasetList,
    dbMapList,
    deleteDataset,
    checkedDatasetItems,
    setCheckedDatasetItems,
  } = props;

  const [checked, setChecked] = useState(false);
  const [modalShow, setModalShow] = useState(false);

  useEffect(() => {
    setChecked(checkedDatasetItems.includes(item.name));
  }, [checkedDatasetItems, item.name]);

  const handleCheckboxChange = (e) => {
    let isChecked = e.target.checked;
    setChecked(isChecked);

    setCheckedDatasetItems(prevCheckedItems => {
      if (isChecked) {
          return [...prevCheckedItems, item.name];
      } else {
          return prevCheckedItems.filter(name => name!== item.name);
      }
    });
  };

  const resetDeleteMethod = () => {
    setModalShow(false);
  };

  const location = useLocation();
  const sampleCount = getDatasetSampleCount(item);
  const mediaType = item.media_type || item.folder_type || "dataset";
  const statusLabel = sampleCount > 0 ? "Ready" : "Empty";
  const statusTone = sampleCount > 0 ? "ready" : "empty";

  let display;
  // table 无 FO samples，不走 EmptyDataset，交给 CustomizedContent 显示 total_samples
  const isTableType = isTableDataset(item);
  
  if (location.pathname !== "/acquisition" && sampleCount === 0 && !isTableType) {
    display = <EmptyDataset />;
  } else {
    display = CustomizedContent && (
      <CustomizedContent
        item={item}
        loadDatasetList={loadDatasetList}
        dbMapList={dbMapList}
      />
    );
  }

  return (
    <div className="card dataset-card" key={item.name}>
      <div className="dataset-card-header">
        <input
          className="form-check-input dataset-card-checkbox"
          type="checkbox"
          checked={checked}
          onChange={handleCheckboxChange}
          aria-label={buttonText(`Select ${item.name}`)}
        />
        <div className="dataset-card-badges">
          <span className={`org-role-badge ${statusTone === "ready" ? "organizer" : "regular"}`}>{pageText(statusLabel)}</span>
          <span className="org-role-badge regular">{pageText(mediaType)}</span>
        </div>
        <button
          onClick={() => setModalShow(true)}
          type="button"
          className="btn-close dataset-card-delete"
          aria-label={buttonText("Delete dataset")}
        ></button>
      </div>
      <div className="card-body dataset-card-body">
        <button
          type="button"
          className="btn btn-light dataset-title-button"
          onClick={() => openFiftyOne(item.name)}
        >
          <h5 className="card-title">{item.name}</h5>
        </button>
        <DatasetCardPreview item={item} sampleCount={sampleCount} />
        {display}
        {/* <div className="dataset-card-footer">
          <button
            type="button"
            className="dataset-view-details"
            onClick={() => {
              const w = window.open("about:blank");
              w.location.href = `http://${window.location.hostname}:5151/datasets/${item.name}`;
            }}
          >
            View Details
          </button>
        </div> */}
      </div>
      <DeleteDoudleConfirmModal
        item={item}
        isTrashGroup={item.group === "Trash"}
        show={modalShow}
        onHide={() => setModalShow(false)}
        onDelete={deleteDataset}
        datasetName={item.name}
        resetDeleteMethod={resetDeleteMethod}
      />
    </div>
  );
});

export { Datasets };
